class CartDrawer extends HTMLElement {
  connectedCallback() {
    this._boundOpen = this._open.bind(this);
    this._boundClose = this._close.bind(this);

    document.addEventListener('cart:open', this._boundOpen);
    document.getElementById('cart-overlay')?.addEventListener('click', this._boundClose);
    this.addEventListener('click', this._onClick.bind(this));
    this.addEventListener('submit', this._onSubmit.bind(this));
    this._attachCloseButton();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:open', this._boundOpen);
  }

  _attachCloseButton() {
    this.querySelector('[data-close]')?.addEventListener('click', this._boundClose);
  }

  _open() {
    this.setAttribute('open', '');
    document.getElementById('cart-overlay')?.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    this.querySelector('[data-close]')?.focus();
  }

  _close() {
    this.removeAttribute('open');
    document.getElementById('cart-overlay')?.classList.add('hidden');
    document.body.style.overflow = '';
  }

  _onClick(e) {
    const btn = e.target.closest('[data-action]');
    if (!btn) return;
    e.preventDefault();
    const { action, key, quantity } = btn.dataset;
    const qty = parseInt(quantity ?? 0);
    if (action === 'increment') this._updateItem(key, qty + 1);
    if (action === 'decrement') this._updateItem(key, Math.max(0, qty - 1));
    if (action === 'remove')    this._updateItem(key, 0);
    if (action === 'remove-discount') {
      const remaining = this._appliedCodes().filter((c) => c !== btn.dataset.code);
      this._applyDiscounts(remaining);
    }
  }

  _onSubmit(e) {
    const form = e.target.closest('[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('input[name="discount"]');
    const code = (input?.value || '').trim();
    if (!code) return;
    const codes = this._appliedCodes();
    if (!codes.includes(code)) codes.push(code);
    this._applyDiscounts(codes, code, form);
  }

  // Codes currently applied, read from the rendered discount pills.
  _appliedCodes() {
    return [...this.querySelectorAll('[data-action="remove-discount"]')].map((b) => b.dataset.code);
  }

  // POST the full code list to Shopify. `newCode`/`form` are only set when
  // the user just submitted one, so we can flag it as invalid inline.
  async _applyDiscounts(codes, newCode, form) {
    const errorEl = this.querySelector('[data-discount-error]');
    const submitBtn = form?.querySelector('[type="submit"]');
    errorEl?.classList.add('hidden');
    if (submitBtn) submitBtn.disabled = true;

    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(','), sections: ['cart-drawer'] }),
      });
      if (!res.ok) return;
      const cart = await res.json();

      if (newCode) {
        const entered = (cart.discount_codes || []).find((d) => d.code === newCode);
        if (entered && entered.applicable === false) {
          errorEl?.classList.remove('hidden');
          if (form) form.querySelector('input[name="discount"]').value = '';
          return;
        }
      }

      await this._rerender();
      this._syncCartCount();
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  async _updateItem(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!res.ok) return;
    await this._rerender();
    this._syncCartCount();
  }

  async _rerender() {
    const res = await fetch('/?sections=cart-drawer');
    if (!res.ok) return;
    const data = await res.json();
    const parser = new DOMParser();
    const doc = parser.parseFromString(data['cart-drawer'], 'text/html');
    const incoming = doc.querySelector('.shopify-section');
    const current  = this.querySelector('.shopify-section');
    if (incoming && current) {
      current.replaceWith(incoming);
    } else if (incoming) {
      this.appendChild(incoming);
    }
    this._attachCloseButton();
  }

  async _syncCartCount() {
    const res = await fetch('/cart.js');
    if (!res.ok) return;
    const cart = await res.json();
    const el = document.getElementById('cart-count');
    if (!el) return;
    el.textContent = cart.item_count;
    el.classList.toggle('hidden', cart.item_count === 0);
    el.setAttribute('aria-label', `${cart.item_count} items in cart`);
  }
}

customElements.define('cart-drawer', CartDrawer);

// Global add-to-cart interceptor.
// Catches any form posting to /cart/add (or routes.cart_add_url) and
// converts it to AJAX so the user lands in the cart drawer instead of /cart.
// Opt out per-form with `data-no-ajax` if a form needs the legacy redirect.
(function () {
  function isCartAddForm(form) {
    if (!form || form.method.toLowerCase() !== 'post') return false;
    if (form.hasAttribute('data-no-ajax')) return false;
    // Forms that already have their own AJAX submit handler attached elsewhere
    if (form.classList.contains('cs-atc-form')) return false;
    if (form.classList.contains('pt-atc-form')) return false;
    const action = (form.getAttribute('action') || '').replace(/\/+$/, '');
    return /\/cart\/add$/.test(action);
  }

  async function handleSubmit(e) {
    const form = e.target;
    if (!isCartAddForm(form)) return;
    e.preventDefault();

    const btn = form.querySelector('[type="submit"]');
    const originalLabel = btn && (btn.dataset.originalLabel || btn.textContent);
    if (btn) {
      btn.dataset.originalLabel = originalLabel;
      btn.disabled = true;
      btn.dataset.busy = '1';
      btn.textContent = 'Adding…';
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
        body: new URLSearchParams(new FormData(form)),
      });

      if (res.ok) {
        const drawer = document.querySelector('cart-drawer');
        await drawer?._rerender();
        await drawer?._syncCartCount();
        document.dispatchEvent(new CustomEvent('cart:open'));
      } else {
        let msg = 'Could not add to cart.';
        try { const data = await res.json(); if (data.description) msg = data.description; } catch (_) {}
        alert(msg);
      }
    } catch (err) {
      alert('Network error adding to cart.');
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = originalLabel;
        delete btn.dataset.busy;
      }
    }
  }

  document.addEventListener('submit', handleSubmit, true);
})();
